import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { DestinationsService } from '../../reducers/services/destinations.service';
import { TabComponent } from '../../shared/components/tab/tab.component';
import { ToolTipComponent } from '../../shared/components/tool-tip/tool-tip.component';
import { openPdfUrlWithLoader} from '../../utils/pdf-loader.util';
import { ScrollToTopButtonComponent } from "../../shared/components/scroll-to-top/scroll-to-top.component";

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';


// ✅ LightGallery
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';
import { HotelDescriptionDialogComponent } from './hotel-description-dialog/hotel-description-dialog.component';
import { HotelAmenitiesDialogComponent } from './hotel-amenities-dialog/hotel-amenities-dialog.component';
import { Overlay } from '@angular/cdk/overlay';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Inject } from '@angular/core';
import { environment } from '../../../environments/environment';

interface Amenity {
  name: string;
  imageUrl: string;
}

// ✅ CHANGE 1: Extended destinationInfo to include name & slug fields from API
interface HotelModel {
  _id: string;
  hotelAmenitiesInfo: Amenity[];
  name: string;
  slug: string;
  galleryInfo: any[];
  destinationInfo: { _id: string; name?: string; slug?: string };
  bookedCount: number;
  viewCount: number;
  description: string;
  factsheetUrl?: string;
}

@Component({
  selector: 'app-hotel-listing-card',
  standalone: true,
  imports: [
    TabComponent,
    RouterLink,
    CommonModule,
    ToolTipComponent,
    ScrollToTopButtonComponent,
    MatDialogModule,
  ],
  templateUrl: './hotel-listing-card.component.html',
  styleUrl: './hotel-listing-card.component.scss',
})
export class HotelListingCardComponent implements OnDestroy {
  @Input() hotel: HotelModel[] = [];
  @Input() destinationId!: any;
  @Output() destinationTabChange = new EventEmitter<any>();
  @ViewChild('dynamicGallery', { static: false })
  dynamicGalleryRef!: ElementRef<HTMLElement>;
  readonly MOBILE_LIMIT = 190;
  readonly DESKTOP_LIMIT = 500;
  readonly MOBILE_AMENITIES_LIMIT = 4;
  readonly DESKTOP_AMENITIES_LIMIT = 8;
  tabs: any[] = [];
  destinations: any[] = [];
  loading = false;
  filteredHotels: any[] = [];
  selectedIndex = 0;

  activeLoadingKey: string | null = null;
  private navTimer: any = null;

  private queryDestinationId: string | null = null;
  private querySlug: string | null = null;
  private paramSub!: Subscription;

  private lgInstance: any;
  private lgReady = false;
  descExpandedMap: Record<string, boolean> = {};
  amenitiesExpandedMap: Record<string, boolean> = {};

  private destroy$ = new Subject<void>();
  isMobile = false;
  hotelDescriptionHtmlMap: Record<string, string> = {};

  constructor(
    private destinationService: DestinationsService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private bp: BreakpointObserver,
    private overlay: Overlay,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit() {
    this.bp.observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.isMobile = res.matches;
        this.prepareHotelDescriptions();
        this.cdr.detectChanges();
      });

    this.filteredHotels = this.withImagesOnly(this.hotel);
    this.prepareHotelDescriptions();

    this.paramSub = this.route.paramMap.subscribe((params) => {
      this.querySlug = params.get('slug');
      this.applyDestinationFilter();
    });

    this.getDestinations();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges['hotel']) {
      this.filteredHotels = this.withImagesOnly(this.hotel);
      this.prepareHotelDescriptions();
      this.applyDestinationFilter();
    }
  }

  ngOnDestroy() {
    this.paramSub?.unsubscribe();
    if (this.navTimer) clearTimeout(this.navTimer);
    this.destroyGallery();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ✅ CHANGE 2: New helper — resolves destination slug for a hotel.
  // Priority: (1) slug from tabs lookup by destinationInfo._id,
  //           (2) slug from destinationInfo directly (if API provides it),
  //           (3) name-based fallback (lowercase, spaces → hyphens).
  getDestinationSlug(hotel: HotelModel): string {
    const destId = hotel?.destinationInfo?._id;

    if (destId) {
      // Look up the tab we already fetched from getDestinations()
      const matchedTab = this.tabs.find((t) => String(t.id) === String(destId));
      if (matchedTab?.slug) {
        return matchedTab.slug;
      }
    }

    // Fallback: use slug field if API returns it on destinationInfo
    if (hotel?.destinationInfo?.slug) {
      return hotel.destinationInfo.slug;
    }

    // Last resort: derive slug from destination name
    const name = hotel?.destinationInfo?.name || '';
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  // ✅ CHANGE 3: Build full hotel URL segments as array for use in templates
  getHotelRouteSegments(hotel: HotelModel): string[] {
    const destSlug = this.getDestinationSlug(hotel);
    if (destSlug) {
      return ['/our-hotels', destSlug, hotel.slug];
    }
    // Fallback to old format if destination is somehow unavailable
    return ['/our-hotels', hotel.slug];
  }

  // ✅ CHANGE 4: Build href string for attr.href (SEO anchor fallback)
  getHotelHref(hotel: HotelModel): string {
    return this.getHotelRouteSegments(hotel).join('/');
  }

  getAmenitiesLimit(): number {
    return this.isMobile ? this.MOBILE_AMENITIES_LIMIT : this.DESKTOP_AMENITIES_LIMIT;
  }

  getVisibleAmenitiesForCard(hotel: HotelModel): Amenity[] {
    const list = hotel?.hotelAmenitiesInfo || [];
    const limit = this.getAmenitiesLimit();

    if (this.isMobile && this.isAmenitiesExpanded(hotel?._id)) return list;

    return list.slice(0, limit);
  }

  shouldShowAmenitiesToggle(hotel: HotelModel): boolean {
    return (hotel?.hotelAmenitiesInfo?.length || 0) > this.getAmenitiesLimit();
  }

  openHotelGallery(hotel: HotelModel, startIndex = 0, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    if (!this.dynamicGalleryRef?.nativeElement) return;

    const items = hotel?.galleryInfo || [];
    if (!items.length) return;

    const dynamicEl = this.buildDynamicElFromGallery(items);
    if (!dynamicEl.length) return;

    const safeIndex = Math.max(0, Math.min(startIndex, dynamicEl.length - 1));

    this.destroyGallery();

    this.lgInstance = lightGallery(this.dynamicGalleryRef.nativeElement, {
      dynamic: true,
      dynamicEl: dynamicEl as any,
      plugins: [lgThumbnail, lgZoom],
      download: false,
      thumbnail: true,
      speed: 400,
      mobileSettings: { controls: true, showCloseIcon: true },
    });

    this.lgReady = true;

    setTimeout(() => this.lgInstance.openGallery(safeIndex), 0);
  }

  private destroyGallery() {
    if (this.lgInstance?.destroy) {
      this.lgInstance.destroy(true);
      this.lgInstance = null;
    }
    this.lgReady = false;
  }

  private buildDynamicElFromGallery(items: any[]) {
    return (items || [])
      .filter((m: any) => !!(m?.cdnUrl || m?.location))
      .map((m: any) => {
        const url = m?.cdnUrl || m?.location || '';
        return { src: url, thumb: url };
      });
  }

  getFirstAmenities(hotel: HotelModel): Amenity[] {
    return hotel.hotelAmenitiesInfo?.slice(0, 4) ?? [];
  }

  getRemainingAmenities(hotel: HotelModel): Amenity[] {
    return hotel.hotelAmenitiesInfo?.slice(4) ?? [];
  }

  private isVideoMedia(item: any): boolean {
    const url: string = item?.cdnUrl || item?.location || '';
    return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
  }

  private withImagesOnly(hotels: any[]): any[] {
    return (hotels || []).map((h) => ({
      ...h,
      galleryInfo: (h.galleryInfo || []).filter((m: any) => !this.isVideoMedia(m)),
    }));
  }

  getDescLimit(): number {
    return this.isMobile ? this.MOBILE_LIMIT : this.DESKTOP_LIMIT;
  }

  getHotelDesc(hotel: HotelModel): string {
    return this.getHotelDescFromText(hotel?.description, hotel);
  }

  applyDestinationFilter() {
    if (!this.tabs?.length) return;

    let matchIndex = 0;

    if (this.destinationId) {
      const byId = this.tabs.findIndex(
        (tab) => String(tab.id) === String(this.destinationId)
      );
      if (byId > -1) {
        matchIndex = byId;
      }
    }

    if (this.querySlug) {
      const bySlug = this.tabs.findIndex(
        (tab) => String(tab.slug) === String(this.querySlug)
      );
      if (bySlug > -1) {
        matchIndex = bySlug;
      }
    }

    this.selectedIndex = matchIndex;
    this.onTabChange(matchIndex, true);
  }

  getDestinations(): void {
    this.loading = true;
    this.destinationService.getDestinationDropDown({ country: 'India' }).subscribe({
      next: (res: any) => {
        this.destinations = res?.data || [];
        this.loading = false;

        const totalHotelCount =
          res?.totalHotelCount ??
          this.destinations.reduce((sum: number, d: any) => sum + (d.hotelCount ?? 0), 0);

        this.tabs = [
          { label: 'All', id: 'all', target: '#all', badgeCount: totalHotelCount },
          ...this.destinations.map((d: any) => ({
            label: d.name,
            id: d._id,
            target: `#${d.slug}-section`,
            badgeCount: d.hotelCount ?? 0,
            slug: d.slug,
          })),
        ];

        if (this.destinationId) {
          const matchIndex = this.tabs.findIndex((tab) => tab.id === this.destinationId);
          if (matchIndex > -1) {
            this.selectedIndex = matchIndex;
            this.onTabChange(matchIndex);
          }
        }
      },
      error: () => (this.loading = false),
    });
  }

  onTabChange(index: number, skipNavigate = false) {
    const selectedTab = this.tabs[index];
    if (!selectedTab) return;

    this.selectedIndex = index;

    if (selectedTab.id === 'all') {
      this.filteredHotels = this.withImagesOnly(this.hotel);
    } else {
      const hotelsForDestination = this.hotel.filter(
        (h: any) => String(h?.destinationInfo?._id) === String(selectedTab.id)
      );
      this.filteredHotels = this.withImagesOnly(hotelsForDestination);
    }

    this.prepareHotelDescriptions();
    this.destinationTabChange.emit(selectedTab);

    if (!skipNavigate) {
      if (selectedTab.slug) {
        this.router.navigate(['/our-hotels', selectedTab.slug]);
      } else {
        this.router.navigate(['/our-hotels']);
      }
    }
  }

  private startLoader(key: string, action: () => void) {
    if (this.navTimer) clearTimeout(this.navTimer);
    this.activeLoadingKey = key;
    this.cdr.detectChanges();

    this.navTimer = setTimeout(() => {
      action();
      this.activeLoadingKey = null;
      this.cdr.detectChanges();
    }, 1000);
  }

viewFactsheet(hotel: HotelModel) {
  if (!isPlatformBrowser(this.platformId)) return;

  const pdfUrl = hotel?.factsheetUrl;

  if (!pdfUrl) {
    console.warn('Factsheet URL not available for hotel:', hotel?.name);
    return;
  }

  const ua = window.navigator.userAgent;

  const isIosSafari =
    /iP(ad|hone|od)/i.test(ua) &&
    /Safari/i.test(ua) &&
    !/CriOS|FxiOS|EdgiOS/i.test(ua);


    if (isIosSafari) {
      window.location.href = pdfUrl;
      return;
    }

    openPdfUrlWithLoader(pdfUrl, 'PDF Loading Please Wait...');
  }

  goToOffers(hotelId: string) {
    const key = `offers:${hotelId}`;
    this.startLoader(key, () => {
      this.router.navigate(['/offers']);
    });
  }

  // ✅ CHANGE 5: goToAvailability now navigates to /our-hotels/:destination/:slug
  goToAvailability(hotel: HotelModel) {
    const key = `availability:${hotel._id}`;
    this.startLoader(key, () => {
      const segments = this.getHotelRouteSegments(hotel);
      this.router.navigate(segments);
    });
  }

  formatCount(value: number | null | undefined): string {
    const n = Number(value ?? 0);

    if (n < 1000) return `${n}`;
    if (n < 1000000) {
      const k = n / 1000;
      const text = k >= 10 ? k.toFixed(0) : k.toFixed(1);
      return `${text.replace(/\.0$/, '')}K`;
    }

    const m = n / 1000000;
    const text = m >= 10 ? m.toFixed(0) : m.toFixed(1);
    return `${text.replace(/\.0$/, '')}M`;
  }

  isDescExpanded(hotelId: string): boolean {
    return !!this.descExpandedMap[hotelId];
  }

  toggleDesc(hotelId: string) {
    this.descExpandedMap[hotelId] = !this.descExpandedMap[hotelId];
  }

  isAmenitiesExpanded(hotelId: string): boolean {
    return !!this.amenitiesExpandedMap[hotelId];
  }

  toggleAmenities(hotelId: string) {
    this.amenitiesExpandedMap[hotelId] = !this.amenitiesExpandedMap[hotelId];
  }

  shouldShowReadMore(desc: string | null | undefined): boolean {
    const text = (desc || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return text.length > this.getDescLimit();
  }

  onReadMoreClick(hotel: HotelModel, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    if (this.isMobile) {
      this.toggleDesc(hotel._id);
      this.setDescriptionHtml(hotel, hotel?.description);
      return;
    }

    this.dialog.open(HotelDescriptionDialogComponent, {
      width: '750px',
      maxWidth: '60vw',
      panelClass: 'hotel-desc-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
      enterAnimationDuration: '200ms',
      exitAnimationDuration: '150ms',
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      data: {
        title: hotel?.name || 'Hotel',
        description: this.getFullHotelDescriptionHtml(hotel?.description),
      },
    });
  }

  onAmenitiesViewMoreClick(hotel: any, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    if (this.isMobile) {
      this.toggleAmenities(hotel._id);
      return;
    }

    this.dialog.open(HotelAmenitiesDialogComponent, {
      width: '750px',
      maxWidth: '60vw',
      panelClass: 'hotel-amenities-dialog-panel',
      autoFocus: false,
      restoreFocus: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      data: {
        title: hotel?.name || 'Hotel',
        hotelAmenitiesInfo: hotel?.hotelAmenitiesInfo || [],
        roomAmenitiesInfo: hotel?.roomAmenitiesInfo || [],
        roomTypes: hotel?.roomTypes || [],
      },
    });
  }

  setDescriptionHtml(hotel: HotelModel, desc?: string | null) {
    const raw = this.getHotelDescFromText(desc, hotel);
    this.hotelDescriptionHtmlMap[hotel._id] = isPlatformBrowser(this.platformId)
      ? this.cleanHotelDescriptionHtml(raw)
      : raw;
  }

  getHotelDescriptionHtml(hotel: HotelModel): string {
    return this.hotelDescriptionHtmlMap[hotel?._id] || '';
  }

  private getHotelDescFromText(desc?: string | null, hotel?: HotelModel): string {
    const value = (desc || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const limit = this.getDescLimit();

    if (this.isMobile && hotel?._id && this.isDescExpanded(hotel._id)) {
      return value;
    }

    return value.length > limit ? value.slice(0, limit) : value;
  }

  decodeHtmlEntities(text: string): string {
    if (!isPlatformBrowser(this.platformId)) return text;

    return this.cleanHotelDescriptionHtml(text);
  }

  private cleanHotelDescriptionHtml(text: string): string {
    let value = (text || '').toString();

    for (let i = 0; i < 5; i++) {
      const decoded = this.decodeOnce(value);
      if (decoded === value) break;
      value = decoded;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = value;

    value = wrapper.innerHTML;

    value = value
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/<p>\s*(<br\s*\/?>|&nbsp;|&#160;|\s)*<\/p>/gi, '')
      .replace(/<span>\s*<\/span>/gi, '')
      .replace(/<span>([^<]*)<\/span>/gi, '$1')
      .replace(/\s{2,}/g, ' ')
      .trim();

    return value;
  }

  private decodeOnce(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  prepareHotelDescriptions() {
    this.hotelDescriptionHtmlMap = {};
    (this.filteredHotels || []).forEach((hotel: HotelModel) => {
      this.setDescriptionHtml(hotel, hotel?.description);
    });
  }

  getFullHotelDescriptionHtml(desc?: string | null): string {
    const raw = (desc ?? '').toString();
    return isPlatformBrowser(this.platformId)
      ? this.cleanHotelDescriptionHtml(raw)
      : raw;
  }

  hasMoreImages(hotel: HotelModel): boolean {
    return (hotel?.galleryInfo?.length || 0) > 4;
  }

  getRemainingImageCount(hotel: HotelModel): number {
    const total = hotel?.galleryInfo?.length || 0;
    return total > 4 ? total - 4 : 0;
  }
}